import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Layers, Loader2, Save, Plus } from 'lucide-react';
import {
  formatAxiosAdminError,
  getAdminCatalogBranches,
  getAdminVitrineCategories,
  getVitrineCategoryConfig,
  putAdminVitrineBranches,
  putAdminVitrineCategories,
  type AdminVitrineBundle,
  type VitrineCategoryConfigItem,
} from '../../api';
import { useHaptic } from '../../hooks/useHaptic';
import { useAppStore } from '../../store';
import { AdminGlassDisclosure } from '@/components/admin/AdminGlassDisclosure';

type Row = VitrineCategoryConfigItem & { branch_id?: number };

/** То же значение что в сборке попало в клиент как base URL API — только для подсказки в UI. */
const BAKED_BACKEND_URL = String(import.meta.env.VITE_BACKEND_URL ?? '').trim();

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** Любой HTTP-ответ (401/403/5xx) — не «чистая сеть»; иначе ложный режим «только публичный конфиг». */
function isLikelyNetworkError(e: unknown): boolean {
  if (!axios.isAxiosError(e)) return false;
  if (e.response) return false;
  return true;
}

/** Cold start / WebView; отдельно ловим гонку JWT после гидрации (401 → повтор). */
async function fetchAdminVitrineWithRetries(): Promise<
  Awaited<ReturnType<typeof getAdminVitrineCategories>>
> {
  let last: unknown;
  for (let attempt = 0; attempt < 5; attempt++) {
    try {
      return await getAdminVitrineCategories();
    } catch (e) {
      last = e;
      if (axios.isAxiosError(e) && e.response?.status === 401 && attempt < 4) {
        await sleep(200 * (attempt + 1));
        continue;
      }
      if (isLikelyNetworkError(e) && attempt < 4) {
        await sleep(450 * 2 ** Math.min(attempt, 2));
        continue;
      }
      throw e;
    }
  }
  throw last;
}

type VitrineCategoryPanelProps = {
  /**
   * Список позиций `/admin/products` уже успешно загрузился — тот же токен и сессия,
   * без гонки «витрина раньше каталога» (типичный источник ложного «Network Error» в WebView).
   */
  catalogListPrimed: boolean;
  /** Пока идёт загрузка таблицы позиций — витрину не дёргаем */
  catalogLoading: boolean;
  /** Ошибка GET /admin/products — показываем подсказку вместо ложного «режима просмотра» */
  catalogFetchFailed: boolean;
  /** Данные витрины из того же ответа что каталог (prefer) или separate / ожидание */
  vitrineFromCatalog: AdminVitrineBundle | 'separate' | null;
  /** Повторить объединённую загрузку каталога+vitrine */
  onReloadCatalog: () => void | Promise<void>;
};

export function VitrineCategoryPanel({
  catalogListPrimed,
  catalogLoading,
  catalogFetchFailed,
  vitrineFromCatalog,
  onReloadCatalog,
}: VitrineCategoryPanelProps) {
  const haptic = useHaptic();
  /** Пока zustand не подтянул token из persist, админ-запрос ушёл бы без Bearer. */
  const [storeReady, setStoreReady] = useState(() => useAppStore.persist.hasHydrated());
  useEffect(() => {
    if (useAppStore.persist.hasHydrated()) {
      setStoreReady(true);
      return;
    }
    return useAppStore.persist.onFinishHydration(() => setStoreReady(true));
  }, []);

  const [panelOpen, setPanelOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newCategoryKey, setNewCategoryKey] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  /** Полный сбой загрузки (админ и публичный запасной путь не сработали). */
  const [fatalError, setFatalError] = useState<string | null>(null);
  /**
   * Админ-роут не ответил, но публичный конфиг загружен: только просмотр, без «обнаруженных» ключей.
   */
  const [degradedNotice, setDegradedNotice] = useState<string | null>(null);

  const isDegradedReadOnly = Boolean(degradedNotice);
  const [rows, setRows] = useState<Row[]>([]);
  const [discovered, setDiscovered] = useState<string[]>([]);

  const waitCatalogList = storeReady && !catalogListPrimed;

  const refreshFromStandaloneEndpoint = useCallback(async () => {
    if (!storeReady || !catalogListPrimed) return;
    setLoading(true);
    setFatalError(null);
    setDegradedNotice(null);
    try {
      const branches = await getAdminCatalogBranches();
      if (branches.length > 0) {
        setRows(
          branches.map((b) => ({
            branch_id: b.id,
            category_key: b.title,
            sort_order: b.sort_order,
            is_visible: b.show_on_vitrine ?? true,
            display_label: b.vitrine_label ?? null,
          }))
        );
        setDiscovered([]);
        return;
      }
      const data = await fetchAdminVitrineWithRetries();
      setRows(
        (data.items ?? []).map((r) => ({
          category_key: r.category_key,
          sort_order: r.sort_order,
          is_visible: r.is_visible,
          display_label: r.display_label ?? null,
        }))
      );
      setDiscovered(data.discovered_keys ?? []);
    } catch (e) {
      console.error(e);
      const isNetwork = isLikelyNetworkError(e);
      if (isNetwork) {
        try {
          const items = await getVitrineCategoryConfig();
          setRows(
            items.map((r) => ({
              category_key: r.category_key,
              sort_order: r.sort_order,
              is_visible: r.is_visible,
              display_label: r.display_label ?? null,
            }))
          );
          setDiscovered([]);
          setDegradedNotice(
            'Отдельный запрос витрины не дошёл до сервера. Ниже — публичный конфиг (просмотр). Нажмите «Обновить каталог» — витрина подтянется вместе с позициями одним запросом (нужен актуальный backend).'
          );
        } catch (e2) {
          console.error(e2);
          setRows([]);
          setDiscovered([]);
          setFatalError(formatAxiosAdminError(e, 'Не удалось загрузить настройки витрины.'));
        }
      } else {
        setRows([]);
        setDiscovered([]);
        setFatalError(formatAxiosAdminError(e, 'Не удалось загрузить настройки витрины.'));
      }
    } finally {
      setLoading(false);
    }
  }, [storeReady, catalogListPrimed]);

  /** Витрина уже в теле GET /admin/products — без второго HTTP. */
  useEffect(() => {
    if (!storeReady || !catalogListPrimed) return;
    if (vitrineFromCatalog === null || vitrineFromCatalog === 'separate') return;
    setFatalError(null);
    setDegradedNotice(null);
    setRows(
      (vitrineFromCatalog.items ?? []).map((r) => ({
        category_key: r.category_key,
        sort_order: r.sort_order,
        is_visible: r.is_visible,
        display_label: r.display_label ?? null,
      }))
    );
    setDiscovered(vitrineFromCatalog.discovered_keys ?? []);
    setLoading(false);
  }, [storeReady, catalogListPrimed, vitrineFromCatalog]);

  /** Старый контракт API: отдельный GET /admin/catalog/vitrine-categories */
  useEffect(() => {
    if (!storeReady || !catalogListPrimed) return;
    if (vitrineFromCatalog !== 'separate') return;
    void refreshFromStandaloneEndpoint();
  }, [storeReady, catalogListPrimed, vitrineFromCatalog, refreshFromStandaloneEndpoint]);

  const updateRow = (key: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.category_key === key ? { ...r, ...patch } : r)));
  };

  const addDiscovered = (key: string) => {
    haptic.selection();
    const maxOrder = rows.reduce((m, r) => Math.max(m, r.sort_order), 0);
    setRows((prev) => [
      ...prev,
      {
        category_key: key,
        sort_order: maxOrder + 10,
        is_visible: true,
        display_label: null,
      },
    ]);
    setDiscovered((d) => d.filter((x) => x !== key));
  };

  const addCustomCategory = () => {
    const key = newCategoryKey.trim();
    if (!key) return;
    if (rows.some((r) => r.category_key === key)) {
      alert(`Категория «${key}» уже есть в списке`);
      return;
    }
    addDiscovered(key);
    setNewCategoryKey('');
    setShowNewCategory(false);
  };

  const removeRow = (key: string) => {
    haptic.selection();
    setRows((prev) => prev.filter((r) => r.category_key !== key));
    setDiscovered((d) => [...d, key].sort((a, b) => a.localeCompare(b, 'ru')));
  };

  const onSave = async () => {
    if (isDegradedReadOnly) return;
    haptic.impact('light');
    setSaving(true);
    setFatalError(null);
    try {
      const branchRows = rows.filter((r) => r.branch_id != null);
      if (branchRows.length === rows.length && branchRows.length > 0) {
        await putAdminVitrineBranches(
          branchRows.map((r) => ({
            id: r.branch_id!,
            sort_order: r.sort_order,
            show_on_vitrine: r.is_visible,
            vitrine_label: r.display_label,
            icon_url: null,
          }))
        );
        const refreshed = await getAdminCatalogBranches();
        setRows(
          refreshed.map((b) => ({
            branch_id: b.id,
            category_key: b.title,
            sort_order: b.sort_order,
            is_visible: b.show_on_vitrine ?? true,
            display_label: b.vitrine_label ?? null,
          }))
        );
        setDiscovered([]);
      } else {
        await putAdminVitrineCategories(rows);
        const data = await getAdminVitrineCategories();
        setRows(
          (data.items ?? []).map((r) => ({
            category_key: r.category_key,
            sort_order: r.sort_order,
            is_visible: r.is_visible,
            display_label: r.display_label ?? null,
          }))
        );
        setDiscovered(data.discovered_keys ?? []);
      }
      haptic.success();
    } catch (e) {
      console.error(e);
      setFatalError(formatAxiosAdminError(e, 'Сохранение не удалось.'));
      haptic.error();
    } finally {
      setSaving(false);
    }
  };

  const canSave = useMemo(
    () => rows.length > 0 && !saving && !isDegradedReadOnly,
    [rows.length, saving, isDegradedReadOnly],
  );

  const badgeCount = useMemo(() => {
    if (waitCatalogList && catalogLoading) return '…';
    if (waitCatalogList && !catalogLoading) return '—';
    if (loading) return '…';
    const n = rows.length;
    const v = rows.filter((r) => r.is_visible).length;
    if (isDegradedReadOnly && n) return `${v}/${n} · просмотр`;
    return n ? `${v}/${n}` : '0';
  }, [waitCatalogList, catalogLoading, loading, rows, isDegradedReadOnly]);

  return (
    <AdminGlassDisclosure
      accent="emerald"
      open={panelOpen}
      onOpenChange={setPanelOpen}
      expandSignal={Boolean(fatalError || degradedNotice)}
      title="Витрина: категории"
      subtitle="Порядок табов, видимость и подпись на главном экране каталога в Telegram."
      icon={<Layers size={16} color="#68D391" />}
      badge={badgeCount}
      headerRight={
        !panelOpen && fatalError ? (
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(252,129,74,0.22)', color: '#FC814A' }}
            title={fatalError}
          >
            !
          </span>
        ) : !panelOpen && degradedNotice ? (
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(99,179,237,0.22)', color: '#63B3ED' }}
            title={degradedNotice}
          >
            i
          </span>
        ) : null
      }
      contentClassName="!pb-2"
    >
      <div className="flex justify-end mb-3">
        <button
          type="button"
          disabled={!canSave}
          onClick={(e) => {
            e.stopPropagation();
            void onSave();
          }}
          title={isDegradedReadOnly ? 'Нужен ответ админ-API' : undefined}
          className="press shrink-0 px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-40"
          style={{
            background: 'rgba(72,187,120,0.2)',
            color: '#68D391',
            border: '1px solid rgba(72,187,120,0.45)',
          }}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Сохранить
        </button>
      </div>

      {degradedNotice && (
        <div
          className="rounded-xl px-3 py-2 text-xs mb-3 space-y-2"
          style={{
            background: 'rgba(99,179,237,0.1)',
            color: 'var(--cyan-bright)',
            border: '1px solid rgba(99,179,237,0.35)',
          }}
        >
          <p className="whitespace-pre-line leading-relaxed">{degradedNotice}</p>
          <p className="text-[10px] opacity-90" style={{ color: 'var(--text-3)' }}>
            API в сборке:{' '}
            <span className="font-mono break-all">{BAKED_BACKEND_URL || '— не задано'}</span>
          </p>
          <button
            type="button"
            onClick={() => {
              haptic.selection();
              void onReloadCatalog();
            }}
            className="press w-full py-2 rounded-lg text-[11px] font-bold"
            style={{ background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
          >
            Обновить каталог и витрину
          </button>
        </div>
      )}

      {fatalError && (
        <div
          className="rounded-xl px-3 py-2 text-xs mb-3 space-y-2"
          style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}
        >
          <p className="whitespace-pre-line leading-relaxed">{fatalError}</p>
          <p className="text-[10px] opacity-90" style={{ color: 'var(--text-3)' }}>
            API в сборке:{' '}
            <span className="font-mono break-all">{BAKED_BACKEND_URL || '— не задано'}</span>
          </p>
          <button
            type="button"
            onClick={() => {
              haptic.selection();
              void onReloadCatalog();
            }}
            className="press w-full py-2 rounded-lg text-[11px] font-bold"
            style={{ background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
          >
            Обновить каталог и витрину
          </button>
        </div>
      )}

      {waitCatalogList && catalogLoading && (
        <div className="flex flex-col items-center justify-center gap-3 py-8 px-2">
          <Loader2 size={22} className="animate-spin" style={{ color: 'var(--cyan)' }} />
          <p className="text-center text-xs leading-relaxed max-w-sm" style={{ color: 'var(--text-3)' }}>
            Загружаем каталог: витрина подставляется из того же ответа сервера (без второго запроса — стабильнее в Telegram WebView).
          </p>
        </div>
      )}

      {waitCatalogList && !catalogLoading && catalogFetchFailed && (
        <div
          className="rounded-xl px-3 py-2.5 text-xs mb-2 leading-relaxed"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-subtle)', color: 'var(--text-2)' }}
        >
          Список позиций не загрузился — витрина ждёт успешный запрос таблицы выше. Исправьте ошибку и повторите.
        </div>
      )}

      {!waitCatalogList && loading && (
        <div className="flex justify-center py-8">
          <Loader2 size={22} className="animate-spin" style={{ color: 'var(--cyan)' }} />
        </div>
      )}

      {!waitCatalogList && !loading && (
        <>
          <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1 touch-pan-y">
            {rows.length === 0 && (
              <p
                className="text-center text-xs py-8 px-2 leading-relaxed rounded-xl"
                style={{ color: 'var(--text-3)', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}
              >
                {fatalError || degradedNotice
                  ? 'Нет строк для отображения.'
                  : 'Конфиг витрины пуст в базе. После миграций на сервере должны появиться строки по умолчанию; либо добавьте категории из блока «обнаруженные» ниже.'}
              </p>
            )}
            {rows.map((r) => (
              <div
                key={r.category_key}
                className="grid grid-cols-12 gap-2 items-center rounded-xl px-3 py-2"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}
              >
                <div className="col-span-12 sm:col-span-4 min-w-0">
                  <p className="text-[10px] uppercase font-bold tracking-wide" style={{ color: 'var(--text-3)' }}>
                    Ключ
                  </p>
                  <p className="text-xs font-semibold truncate" style={{ color: 'var(--text-1)' }} title={r.category_key}>
                    {r.category_key}
                  </p>
                </div>
                <label className="col-span-4 sm:col-span-2 flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-3)' }}>
                    Порядок
                  </span>
                  <input
                    type="number"
                    className="input-glass w-full rounded-lg px-2 py-1.5 text-xs"
                    readOnly={isDegradedReadOnly}
                    value={r.sort_order}
                    onChange={(e) =>
                      updateRow(r.category_key, { sort_order: Number(e.target.value) || 0 })
                    }
                  />
                </label>
                <label className="col-span-4 sm:col-span-2 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    disabled={isDegradedReadOnly}
                    checked={r.is_visible}
                    onChange={(e) => updateRow(r.category_key, { is_visible: e.target.checked })}
                    className="rounded border-white/20"
                  />
                  <span className="text-xs" style={{ color: 'var(--text-2)' }}>
                    Видна
                  </span>
                </label>
                <label className="col-span-12 sm:col-span-3 flex flex-col gap-0.5 min-w-0">
                  <span className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-3)' }}>
                    Подпись (опц.)
                  </span>
                  <input
                    type="text"
                    className="input-glass w-full rounded-lg px-2 py-1.5 text-xs"
                    readOnly={isDegradedReadOnly}
                    placeholder={r.category_key}
                    value={r.display_label ?? ''}
                    onChange={(e) =>
                      updateRow(r.category_key, {
                        display_label: e.target.value.trim() ? e.target.value : null,
                      })
                    }
                  />
                </label>
                <div className="col-span-12 sm:col-span-1 flex justify-end">
                  <button
                    type="button"
                    disabled={isDegradedReadOnly}
                    onClick={() => removeRow(r.category_key)}
                    className="press text-[10px] px-2 py-1 rounded-lg disabled:opacity-40"
                    style={{ color: 'var(--text-3)', border: '1px solid var(--border-subtle)' }}
                  >
                    Убрать
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Add custom category */}
          {!isDegradedReadOnly && (
            <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
              {showNewCategory ? (
                <div className="flex gap-2">
                  <input
                    className="input-glass flex-1 px-2 py-1.5 rounded-lg text-xs"
                    placeholder="Ключ категории (например: accessories)"
                    value={newCategoryKey}
                    autoFocus
                    onChange={(e) => setNewCategoryKey(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addCustomCategory();
                      if (e.key === 'Escape') { setShowNewCategory(false); setNewCategoryKey(''); }
                    }}
                  />
                  <button
                    type="button"
                    disabled={!newCategoryKey.trim()}
                    onClick={addCustomCategory}
                    className="press px-3 py-1.5 rounded-lg text-[11px] font-bold disabled:opacity-40"
                    style={{ background: 'rgba(72,187,120,0.22)', color: '#68D391', border: '1px solid rgba(72,187,120,0.45)' }}
                  >
                    ✓
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowNewCategory(false); setNewCategoryKey(''); }}
                    className="press px-2 py-1.5 rounded-lg"
                    style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => { haptic.selection(); setShowNewCategory(true); }}
                  className="press flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold"
                  style={{ background: 'rgba(99,179,237,0.12)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.3)' }}
                >
                  <Plus size={12} /> Добавить свою категорию
                </button>
              )}
            </div>
          )}

          {discovered.length > 0 && (
            <div className="mt-4 pt-3 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
              <p className="text-[11px] font-bold mb-2" style={{ color: 'var(--text-3)' }}>
                В данных есть категории без строки в таблице — добавьте:
              </p>
              <div className="flex flex-wrap gap-2">
                {discovered.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => addDiscovered(k)}
                    className="press px-2.5 py-1 rounded-lg text-[11px] font-semibold"
                    style={{
                      background: 'rgba(99,179,237,0.12)',
                      color: '#63B3ED',
                      border: '1px solid rgba(99,179,237,0.35)',
                    }}
                  >
                    + {k}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </AdminGlassDisclosure>
  );
}
